import { useCallback, useRef } from 'react';
import type { FC } from 'react';

import type { ForwardProps } from '../../../../types';
import { useI18n, useFocusWithin, useConfiguration, useTestIds } from '../../../../hooks';
import { msToTimeFormat, timeFormatToMs } from '../utils';
import StyledDateTime from '../DateTime.styles';
import { StyledFormControl } from '../../../FormControl';
import Flex from '../../../Flex';
import PartInput from '../PartInput';
import useAutoFocusNextInput from '../useAutoFocusNextInput';
import { getDurationInputTestIds } from '../../DateTime.test-ids';

import type DurationInputProps from './DurationInput.types';

type TimeProps = DurationInputProps & ForwardProps;

const Time: FC<TimeProps> = ({
  testId,
  value,
  onChange,
  disabled,
  readOnly,
  required,
  status,
  autoFocus,
  ...restProps
}: DurationInputProps) => {
  const testIds = useTestIds(testId, getDurationInputTestIds);

  const { locale } = useConfiguration();

  const time =
    value !== undefined
      ? msToTimeFormat(value)
      : { hour: undefined, minute: undefined, second: undefined };

  const [hourInputRef, minuteInputRef, secondInputRef] = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null)
  ];

  // Description is handled in DurationInput which wraps this component.
  useAutoFocusNextInput([hourInputRef, minuteInputRef, secondInputRef]);

  const pickParts = () => {
    return [
      hourInputRef.current?.value || undefined,
      minuteInputRef.current?.value || undefined,
      secondInputRef.current?.value || undefined
    ];
  };

  const onFocusChange = useCallback(
    (focused: boolean) => {
      if (!focused && !disabled && !readOnly && onChange) {
        const parts = pickParts();
        onChange(
          parts.some(v => v !== undefined)
            ? timeFormatToMs({
                hour: Number(parts[0] ?? 0),
                minute: Number(parts[1] ?? 0),
                second: Number(parts[2] ?? 0)
              })
            : undefined
        );
      }
    },
    [disabled, readOnly, onChange]
  );

  const t = useI18n();

  const containerRef = useRef<HTMLElement>(null);
  useFocusWithin([containerRef], onFocusChange);

  const displayNames = new Intl.DisplayNames(locale, { style: 'long', type: 'dateTimeField' });

  return (
    <Flex
      data-testid={testIds.root}
      as={StyledDateTime}
      forwardedAs={StyledFormControl}
      container={{ alignItems: 'center', wrap: 'nowrap' }}
      status={status}
      readOnly={readOnly}
      disabled={disabled}
      ref={containerRef}
      onClick={(e: MouseEvent) => {
        if (e.target === containerRef.current) hourInputRef.current?.focus();
      }}
      {...restProps}
    >
      <PartInput
        data-testid={testIds.controlHour}
        key='hour'
        placeholder={t('hour_placeholder')}
        aria-label={displayNames.of('hour')}
        value={time.hour}
        readOnly={readOnly}
        ref={hourInputRef}
        autoFocus={autoFocus}
      />
      :
      <PartInput
        data-testid={testIds.controlMinute}
        key='minute'
        placeholder={t('minute_placeholder')}
        aria-label={displayNames.of('minute')}
        value={time.minute}
        size={2}
        maxLength={2}
        readOnly={readOnly}
        max={59}
        ref={minuteInputRef}
      />
      :
      <PartInput
        data-testid={testIds.controlSecond}
        key='second'
        placeholder={t('second_placeholder')}
        aria-label={displayNames.of('second')}
        value={time.second}
        size={2}
        maxLength={2}
        readOnly={readOnly}
        max={59}
        ref={secondInputRef}
      />
    </Flex>
  );
};
export default Time;
